import { clsx } from "clsx";
import { Laptop, Monitor, Smartphone } from "lucide-react";
import type { ActiveSession } from "@/types/settings";
import { Button } from "../common";

interface SessionItemProps {
  session: ActiveSession;
  onRevoke: (id: string) => void;
  isRevoking?: boolean;
}

function getDeviceIcon(device: string) {
  const d = device.toLowerCase();
  if (d.includes("iphone") || d.includes("android") || d.includes("phone")) {
    return <Smartphone size={20} aria-hidden="true" />;
  }
  if (d.includes("macbook") || d.includes("laptop")) {
    return <Laptop size={20} aria-hidden="true" />;
  }
  return <Monitor size={20} aria-hidden="true" />;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) {
    return "Teraz";
  }
  if (minutes < 60) {
    return `${minutes} min temu`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} godz. temu`;
  }
  const days = Math.floor(hours / 24);
  return `${days} dn. temu`;
}

export function SessionItem({
  session,
  onRevoke,
  isRevoking = false,
}: SessionItemProps) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-edge-soft px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="text-faint">{getDeviceIcon(session.device)}</div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-main">
              {session.device} &middot; {session.browser}
            </span>
            {session.isCurrent && (
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700">
                Obecna sesja
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-dim">
            {session.ip} &middot; {session.location} &middot;{" "}
            {formatRelativeTime(session.lastActive)}
          </p>
        </div>
      </div>

      {!session.isCurrent && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            onRevoke(session.id);
          }}
          disabled={isRevoking}
          className={clsx(
            "shrink-0 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20",
          )}
        >
          Zakończ
        </Button>
      )}
    </div>
  );
}
